import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import Button from '../../shared/components/ui/Button';
import FormInput from '../../shared/components/ui/FormInput';
import cancelIcon from '../../shared/assets/svg/cancel_icon.svg';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { mergedStagePercentTotal, stagePercentCapMessage, isAdvanceStageName } from '../utils/stagePercentCap';

const EditPaymentStages = ({
  handleOnEditPaymentStage,
  allPaymentStages,
  singleStageData = null,
  onClose,
  isSubmitting,
  blockType = ''
}) => {
  const userId = Cookies.get('userId');
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [deletedStageIds, setDeletedStageIds] = useState([]);
  const isSingleStageMode = !!singleStageData;

  // Setup react-hook-form
  const { register, handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: {
      payment: []
    }
  });

  // UseFieldArray for dynamic fields
  const { fields: paymentStageFields, remove: removePaymentStage } = useFieldArray({
    control,
    name: 'payment'
  });

  // Watch fields for total percentage calculation
  const watchPaymentFields = watch('payment');

  // Initialize form with existing payment stages

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Payment Stages Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (isSingleStageMode && singleStageData) {
      // Single stage editing mode
      reset({
        payment: [
          {
            id: singleStageData.id,
            name: singleStageData.name,
            percentage: singleStageData.percentage,
            is_completed: singleStageData.is_completed,
            unitwise: singleStageData.unitwise || false
          }
        ]
      });
    } else if (allPaymentStages && allPaymentStages.length > 0) {
      // Bulk editing mode
      const mappedStages = allPaymentStages.map((stage) => ({
        id: stage.id,
        name: stage.name,
        percentage: stage.percentage,
        is_completed: stage.is_completed,
        unitwise: stage.unitwise || false
      }));
      reset({ payment: mappedStages });
    }
  }, [allPaymentStages, singleStageData, reset, isSingleStageMode]);

  // Calculate total percentage (single-stage edit still counts sibling stages)
  useEffect(() => {
    const formTotal = watchPaymentFields.reduce(
      (acc, curr) => acc + (parseFloat(curr.percentage) || 0),
      0
    );
    if (isSingleStageMode && allPaymentStages?.length) {
      const edited = watchPaymentFields[0];
      const others = allPaymentStages.filter((s) => s.id !== edited?.id);
      const { total } = mergedStagePercentTotal(
        others,
        edited ? [{ name: edited.name, percentage: edited.percentage }] : []
      );
      setTotalPercentage(total);
      return;
    }
    setTotalPercentage(formTotal);
  }, [watchPaymentFields, isSingleStageMode, allPaymentStages]);

  // Handle percentage changes
  const handlePercentageChange = (index, event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    setValue(`payment.${index}.percentage`, value);
  };

  // Handle stage removal (only for bulk edit mode)
  const handleRemoveStage = (index, stageId) => {
    if (isAdvanceStageName(watchPaymentFields[index]?.name)) {
      return;
    }
    if (stageId) {
      setDeletedStageIds((prev) => [...prev, stageId]);
    }
    removePaymentStage(index);
  };

  // Form submission
  const onSubmit = (data) => {
    if (totalPercentage > 100) {
      toast.error(stagePercentCapMessage(totalPercentage));
      return;
    }
    if (isSingleStageMode) {
      // Single stage edit mode
      const stage = data.payment[0];
      const payload = {
        id: stage.id,
        name: stage.name,
        percentage: parseFloat(stage.percentage)
      };

      // Include unitwise if blockType is Group
      if (blockType === 'Group') {
        payload.unitwise = stage.unitwise;
      }

      handleOnEditPaymentStage(payload);
    } else {
      // Bulk edit mode
      const updatedStages = data.payment.map((stage) => {
        const updatedStage = {
          id: stage.id,
          name: stage.name,
          percentage: parseFloat(stage.percentage)
        };

        // Include unitwise if blockType is Group
        if (blockType === 'Group') {
          updatedStage.unitwise = stage.unitwise;
        }

        return updatedStage;
      });

      handleOnEditPaymentStage({
        updatedStages,
        deletedStageIds
      });
    }
  };

  return (
    <div className="mt-10">
      <div className="max-h-[30rem] overflow-y-auto hide-scrollbar">
        <table className="w-full table-auto border rounded-lg">
          <thead className="bg-gray-200 !text-gray-600 rounded-t-xl">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Percentage</th>
              {blockType === 'Group' && <th className="px-4 py-2">Unitwise</th>}
              {!isSingleStageMode && <th className="px-4 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paymentStageFields?.map((field, index) => {
              const isCompleted = watchPaymentFields[index]?.is_completed;
              const isAdvance = isAdvanceStageName(watchPaymentFields[index]?.name);
              return (
                <tr key={field.id} className={isCompleted ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2">
                    <FormInput
                      register={register}
                      register_key={`payment.${index}.name`}
                      type="text"
                      placeholder={`Payment ${index + 1}`}
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      {...register(`payment.${index}.percentage`)}
                      type="number"
                      placeholder="Percentage"
                      min="0"
                      max="100"
                      step="0.01"
                      onChange={(e) => handlePercentageChange(index, e)}
                      className="border rounded p-2 w-full"
                      disabled={isCompleted}
                    />
                  </td>
                  {blockType === 'Group' && (
                    <td className="px-4 py-2">
                      <input type="hidden" {...register(`payment.${index}.unitwise`)} />
                      <select
                        onChange={(e) =>
                          setValue(`payment.${index}.unitwise`, e.target.value === 'true', {
                            shouldValidate: true
                          })
                        }
                        value={watchPaymentFields[index]?.unitwise ? 'true' : 'false'}
                        disabled={isCompleted}
                        className="border rounded p-2 w-full">
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </td>
                  )}
                  {!isSingleStageMode && (
                    <td className="px-4">
                      {!isCompleted && !isAdvance && (
                        <div
                          className="ml-6"
                          onClick={() => handleRemoveStage(index, watchPaymentFields[index]?.id)}>
                          <img className="cursor-pointer" src={cancelIcon} alt="cancel" />
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!isSingleStageMode && (
        <div className="mt-4">
          <p>Total Percentage: {totalPercentage.toFixed(2)}%</p>
          {totalPercentage > 100 && (
            <p className="text-red-500">{stagePercentCapMessage(totalPercentage)}</p>
          )}
        </div>
      )}
      {isSingleStageMode && totalPercentage > 100 && (
        <p className="mt-4 text-red-500">{stagePercentCapMessage(totalPercentage)}</p>
      )}

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button displayType="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default EditPaymentStages;
